import React, { type PropsWithChildren } from "react"
import { Toast, Toaster, createToaster, Portal } from "@ark-ui/react"
import {
  InfoFilledIcon,
  CheckCircleIcon,
  ContextualWarningCircleFilledIcon,
  IconColorsType,
} from "../Icon"
import cx from "classnames"

const DEFAULT_TIMEOUT = 3000
const MAX_TEXT_TIMEOUT = 9000

export type AlertStatus = "info" | "success" | "error"

const computeTimeDurationFromText = (
  content: string | React.ReactNode
): number => {
  if (typeof content !== "string") return DEFAULT_TIMEOUT
  return Math.min(
    Math.max(DEFAULT_TIMEOUT, content.length * 90),
    MAX_TEXT_TIMEOUT
  )
}

const toaster = createToaster({
  overlap: true,
  gap: 12,
  placement: "bottom",
  max: 12,
})

export const useAlerts = () => {
  return {
    sendAlert: ({
      message,
      status,
      duration,
    }: {
      message: React.ReactNode
      status: AlertStatus
      duration?: number
    }) =>
      toaster.create({
        description: message,
        type: status,
        duration: duration ?? computeTimeDurationFromText(message),
      }),
  }
}

const STATUS_ICONS_MAP: { [k in AlertStatus]: typeof InfoFilledIcon } = {
  info: InfoFilledIcon,
  success: CheckCircleIcon,
  error: ContextualWarningCircleFilledIcon,
}

const STATUS_ICON_COLOR_MAP: { [k in AlertStatus]: IconColorsType } = {
  info: "tertiary",
  success: "success",
  error: "error",
}

const Alerter = ({ children }: PropsWithChildren) => {
  return (
    <>
      {children}
      <Portal>
        <Toaster toaster={toaster} className="cobalt-alerter">
          {(toast) => {
            const status = toast.type as AlertStatus
            const IconComp = STATUS_ICONS_MAP[status]
            return (
              <Toast.Root className={cx("cobalt-alert")} key={toast.id}>
                <div className="cobalt-alert__wrapper">
                  <IconComp size={20} color={STATUS_ICON_COLOR_MAP[status]} />
                  <Toast.Description>{toast.description}</Toast.Description>
                </div>
              </Toast.Root>
            )
          }}
        </Toaster>
      </Portal>
    </>
  )
}

export default Alerter
